import { CKEditor } from "@ckeditor/ckeditor5-react"
import {
  Alignment,
  AutoLink,
  Bold,
  ClassicEditor,
  Code,
  Font,
  GeneralHtmlSupport,
  Heading,
  Image,
  ImageCaption,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Italic,
  Link,
  List,
  ListProperties,
  MediaEmbed,
  SimpleUploadAdapter,
  Strikethrough,
  Table,
  TableCellProperties,
  TableProperties,
  TableToolbar,
  Underline,
} from "ckeditor5"

import "ckeditor5/ckeditor5.css"
import { useCallback, useEffect, useRef, useState } from "react"
import { CaptionAlignment } from "./ckeditor-plugins/CaptionAlignment"
import { ImageDimensionsPlugin } from "./ckeditor-plugins/ImageDimensions"
import { applyHeadingInlineStyles } from "./utils"


const LICENSE_KEY = "GPL"
const CKBOX_TOKEN_URL = ""

interface CKEditorProps {
  value: string
  onChange: (data: string) => void
  onInsertMedia?: (callback: (html: string) => void, saveSelection?: () => void) => void
}

const CKEditorComponent = ({ value, onChange, onInsertMedia }: CKEditorProps) => {
  const [editorInstance, setEditorInstance] = useState<any>(null)
  const savedSelectionRef = useRef<any>(null)

  // NO automatic selection tracking - only save when explicitly called

  const insertHtmlAtCursor = useCallback((html: string) => {
    if (editorInstance) {
      try {
        const domParser = new DOMParser()
        const doc = domParser.parseFromString(html, 'text/html')
        const img = doc.querySelector('img')
        const video = doc.querySelector('video')


        // Focus editor view
        const view = editorInstance.editing.view
        view.focus()

        // Try to get saved position first, otherwise use current selection
        let insertPosition = null

        if (savedSelectionRef.current) {
          const { path, offset } = savedSelectionRef.current

          try {
            // Get the root element
            const root = editorInstance.model.document.getRoot()

            // Use the correct CKEditor 5 API: createPositionFromPath(root, path)
            insertPosition = editorInstance.model.createPositionFromPath(root, path)
          } catch (e) {

            // Fallback: try to create position based on the last element in path
            try {
              const root = editorInstance.model.document.getRoot()
              if (path.length > 0) {
                const elementIndex = path[0]
                const element = root.getChild(elementIndex)
                if (element) {
                  insertPosition = editorInstance.model.createPositionAfter(element)
                }
              }
            } catch (e2) {
            }
          }
        }

        // If no saved position, use current selection
        if (!insertPosition) {
          const modelSelection = editorInstance.model.document.selection
          insertPosition = modelSelection.getFirstPosition()
        }

        editorInstance.model.change((writer: any) => {
          if (img) {
            const imageElement = writer.createElement('imageBlock', {
              src: img.getAttribute('src'),
              alt: img.getAttribute('alt') || '',
            })
            editorInstance.model.insertContent(imageElement, insertPosition)
          } else if (video) {
            const videoElement = writer.createElement('$rawElement', {
              html: video.outerHTML,
              priority: 5,
              allowWhere: '$block',
            })
            editorInstance.model.insertContent(videoElement, insertPosition)
          }
        })

        // Clear saved selection after insert
        savedSelectionRef.current = null
      } catch (e) {
        console.error("CKEditor insert error:", e)
      }
    }
  }, [editorInstance])

  // Function to manually save selection (call this before opening modal)
  const saveCurrentSelection = useCallback(() => {
    if (editorInstance) {
      const selection = editorInstance.model.document.selection
      const position = selection.getFirstPosition()
      if (position) {
        savedSelectionRef.current = {
          path: [...position.path],
          offset: position.offset
        }
      }
    }
  }, [editorInstance])

  useEffect(() => {
    if (onInsertMedia && editorInstance) {
      onInsertMedia(insertHtmlAtCursor, saveCurrentSelection)
    }
  }, [editorInstance, onInsertMedia, saveCurrentSelection])

  return (
    <div className="relative">
      <CKEditor
        editor={ClassicEditor}
        onChange={(event: any, editor: ClassicEditor) => {
          const data = editor.getData()
          onChange(applyHeadingInlineStyles(data))
        }}
        onReady={(editor: any) => {
          setEditorInstance(editor)
        }}
        data={value}
        config={{
          licenseKey: LICENSE_KEY,
          plugins: [
            GeneralHtmlSupport,
            Font,
            Heading,
            Alignment,
            Image,
            ImageResize,
            ImageCaption,
            ImageTextAlternative,
            Link,
            AutoLink,
            Bold,
            Code,
            Italic,
            ImageUpload,
            Strikethrough,
            Underline,
            Table,
            TableToolbar,
            TableProperties,
            TableCellProperties,
            SimpleUploadAdapter,
            Image,
            ImageToolbar,
            ImageStyle,
            List,
            ListProperties,
            MediaEmbed,
            ImageDimensionsPlugin,
            CaptionAlignment,
            ...(CKBOX_TOKEN_URL ? ["CKBox"] : []),
            ...(LICENSE_KEY !== "GPL" ? ["SlashCommand"] : []),
          ],
          toolbar: [
            "heading",
            "bold",
            "italic",
            "underline",
            "strikethrough",
            "alignment",
            "insertTable",
            "link",
            "|",
            "mediaEmbed",
            "|",
            {
              label: "Fonts",
              icon: false,
              items: ["fontSize", "fontFamily", "fontColor", "fontBackgroundColor"],
            },
            {
              label: "Lists",
              icon: false,
              items: ["bulletedList", "numberedList"],
            },
          ],
          list: {},
          heading: {
            options: [
              { model: "paragraph", title: "Paragraph", class: "ck-heading_paragraph" },
              { model: "heading1", view: "h1", title: "Heading 1", class: "ck-heading_heading1" },
              { model: "heading2", view: "h2", title: "Heading 2", class: "ck-heading_heading2" },
              { model: "heading3", view: "h3", title: "Heading 3", class: "ck-heading_heading3" },
              { model: "heading4", view: "h4", title: "Heading 4", class: "ck-heading_heading4" },
            ],
          },
          fontFamily: {
            options: [
              "default",
              "Arial, Helvetica, sans-serif",
              "Courier New, Courier, monospace",
              "Georgia, serif",
              "Lucida Sans Unicode, Lucida Grande, sans-serif",
              "Tahoma, Geneva, sans-serif",
              "Times New Roman, Times, serif",
              "Trebuchet MS, Helvetica, sans-serif",
              "Verdana, Geneva, sans-serif",
              "Amazone", // Thêm font Amazone
              "Open Sans, sans-serif", // Thêm font Open Sans
            ],
          },
          fontSize: {
            options: [12, 14, 16, 18, 20, 22, 24, 28, 32, 36],
            supportAllValues: true,
          },
          alignment: {
            options: ["left", "center", "right", "justify"],
          },
          image: {
            resizeUnit: "px",
            styles: {
              options: [
                {
                  name: "alignLeft",
                  title: "Căn trái",
                  icon: "left",
                  className: "image-align-left",
                  modelElements: ["imageBlock", "imageInline"],
                },
                {
                  name: "alignCenter",
                  title: "Căn giữa",
                  icon: "center",
                  className: "image-align-center",
                  modelElements: ["imageBlock", "imageInline"],
                },
                {
                  name: "alignRight",
                  title: "Căn phải",
                  icon: "right",
                  className: "image-align-right",
                  modelElements: ["imageBlock", "imageInline"],
                },
                "side",
              ],
            },
            toolbar: [
              "imageStyle:alignLeft",
              "imageStyle:alignCenter",
              "imageStyle:alignRight",
              "|",
              "imageTextAlternative",
              "toggleImageCaption",
              "|",
              "captionAlignDropdown",
              "|",
              "imageDimensions",
            ],
          },
          table: {
            contentToolbar: ["tableColumn", "tableRow", "mergeTableCells", "tableProperties", "tableCellProperties"],
            tableProperties: {},
            tableCellProperties: {},
          },
          link: {
            addTargetToExternalLinks: false,
            defaultProtocol: "https://",
            decorators: {
              openInNewTab: {
                mode: "manual",
                label: "Mở trong tab mới",
                defaultValue: false,
                attributes: {
                  target: "_blank",
                  rel: "noopener noreferrer",
                },
              },
              nofollow: {
                mode: "manual",
                label: "Nofollow",
                defaultValue: false,
                attributes: {
                  rel: "nofollow",
                },
              },
              openInNewTabAndNofollow: {
                mode: "manual",
                label: "Mở trong tab mới và nofollow",
                defaultValue: false,
                attributes: {
                  rel: "noopener noreferrer nofollow",
                  target: "_blank",
                },
              },
            },
          },
          mediaEmbed: {
            providers: [
              {
                name: "YouTube",
                url: [
                  /^youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/,
                  /^youtube\.com\/embed\/([a-zA-Z0-9_-]+)/,
                  /^youtube\.com\/v\/([a-zA-Z0-9_-]+)/,
                  /^youtu\.be\/([a-zA-Z0-9_-]+)/,
                  /^youtube\.com\/shorts\/([a-zA-Z0-9_-]+)/,
                ],
                html: (match) => {
                  const videoId = match[1]
                  return `<div style="position:relative;padding-bottom:56.25%;height:0;overflow:hidden;margin:16px 0;">
                    <iframe src="https://www.youtube.com/embed/${videoId}" 
                      style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;" 
                      allowfullscreen 
                      loading="lazy">
                    </iframe>
                  </div>`
                },
              },
              {
                name: "TikTok",
                url: [
                  /^tiktok\.com\/@[\w]+\/video\/(\d+)/,
                  /^www\.tiktok\.com\/@[\w]+\/video\/(\d+)/,
                  /^vm\.tiktok\.com\/([a-zA-Z0-9_-]+)/,
                ],
                html: (match) => {
                  const videoId = match[1] || match[0]
                  return `<div style="position:relative;padding-bottom:56.25%;height:0;overflow:hidden;margin:16px 0;">
                    <iframe src="https://www.tiktok.com/embed/${videoId}" 
                      style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;" 
                      allowfullscreen 
                      loading="lazy">
                    </iframe>
                  </div>`
                },
              },
              {
                name: "Facebook",
                url: [
                  /facebook\.com\/.*\/videos\/([a-zA-Z0-9]+)/,
                  /facebook\.com\/reel\/([a-zA-Z0-9]+)/,
                  /facebook\.com\/video\.php\?v=([a-zA-Z0-9]+)/,
                  /fb\.watch\/([a-zA-Z0-9_-]+)/,
                ],
                html: (match) => {
                  const videoId = match[1]
                  return `<div style="position:relative;padding-bottom:56.25%;height:0;overflow:hidden;margin:16px 0;">
                      <iframe src="https://www.facebook.com/plugins/video.php?href=https://www.facebook.com/video.php?v=${videoId}" 
                        style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;" 
                        allowfullscreen 
                        loading="lazy">
                      </iframe>
                    </div>`
                },
              },
            ],
          },
          htmlSupport: {
            allow: [
              {
                name: /^video$/,
                attributes: true,
              },
              {
                name: /^(img|picture)$/,
                attributes: true,
                styles: true,
              },
              {
                name: /^figure$/,
                attributes: true,
                classes: true,
              },
            ],
          },
          ckbox: {
            tokenUrl: CKBOX_TOKEN_URL,
          },
        }}
      />
    </div>
  )
}

export default CKEditorComponent
